import { useState, useEffect, useMemo } from "react";
import { 
  Search, TrendingUp, AlertCircle, CheckCircle, 
  Eye, Globe, Twitter, ChevronDown, ChevronUp,
  Target, FileText, Link2, Image as ImageIcon
} from "lucide-react";

const SEOPanel = ({ data, onChange, content = "", title = "" }) => {
  const [expanded, setExpanded] = useState({
    general: true,
    social: false,
    advanced: false
  });

  // Calculate SEO score
  const seoScore = useMemo(() => {
    let score = 0;
    const checks = [];

    // Focus keyword
    if (data.focus_keyword) {
      score += 10;
      checks.push({ label: "Palavra-chave definida", passed: true });
      
      // Check if keyword is in title
      if (title.toLowerCase().includes(data.focus_keyword.toLowerCase())) {
        score += 15;
        checks.push({ label: "Palavra-chave no título", passed: true });
      } else {
        checks.push({ label: "Palavra-chave no título", passed: false });
      }
      
      // Check if keyword is in content
      if (content.toLowerCase().includes(data.focus_keyword.toLowerCase())) {
        score += 15;
        checks.push({ label: "Palavra-chave no conteúdo", passed: true });
      } else {
        checks.push({ label: "Palavra-chave no conteúdo", passed: false });
      }
    } else {
      checks.push({ label: "Palavra-chave definida", passed: false });
    }

    // Meta title
    if (data.meta_title) {
      const titleLength = data.meta_title.length;
      if (titleLength >= 30 && titleLength <= 60) {
        score += 15;
        checks.push({ label: "Meta título com tamanho ideal (30-60 caracteres)", passed: true });
      } else {
        score += 5;
        checks.push({ label: "Meta título com tamanho ideal (30-60 caracteres)", passed: false });
      }
    } else {
      checks.push({ label: "Meta título definido", passed: false });
    }

    // Meta description
    if (data.meta_description) {
      const descLength = data.meta_description.length;
      if (descLength >= 120 && descLength <= 160) {
        score += 15;
        checks.push({ label: "Meta descrição com tamanho ideal (120-160 caracteres)", passed: true });
      } else {
        score += 5;
        checks.push({ label: "Meta descrição com tamanho ideal (120-160 caracteres)", passed: false });
      }
    } else {
      checks.push({ label: "Meta descrição definida", passed: false });
    }

    // Content length
    const wordCount = content.replace(/<[^>]*>/g, '').split(/\s+/).filter(w => w).length;
    if (wordCount >= 300) {
      score += 15;
      checks.push({ label: "Conteúdo com mais de 300 palavras", passed: true });
    } else {
      checks.push({ label: "Conteúdo com mais de 300 palavras", passed: false });
    }

    // OG Image
    if (data.og_image) {
      score += 10;
      checks.push({ label: "Imagem de compartilhamento definida", passed: true });
    } else {
      checks.push({ label: "Imagem de compartilhamento definida", passed: false });
    }

    // Canonical URL
    if (data.canonical_url) {
      score += 5;
      checks.push({ label: "URL canônica definida", passed: true });
    }

    return { score: Math.min(100, score), checks };
  }, [data, content, title]);

  const getScoreColor = (score) => {
    if (score >= 80) return "text-green-500";
    if (score >= 50) return "text-yellow-500";
    return "text-red-500";
  };

  const getScoreBg = (score) => {
    if (score >= 80) return "bg-green-100 dark:bg-green-900/30";
    if (score >= 50) return "bg-yellow-100 dark:bg-yellow-900/30";
    return "bg-red-100 dark:bg-red-900/30";
  };

  const handleChange = (field, value) => {
    onChange({ ...data, [field]: value });
  };

  const toggleSection = (section) => {
    setExpanded(prev => ({ ...prev, [section]: !prev[section] }));
  };

  return (
    <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
      {/* Header with Score */}
      <div className="p-4 border-b border-slate-200 dark:border-slate-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Target className="w-5 h-5 text-primary" />
            <h3 className="font-semibold text-slate-800 dark:text-slate-100">Otimização SEO</h3>
          </div>
          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${getScoreBg(seoScore.score)}`}>
            <TrendingUp className={`w-4 h-4 ${getScoreColor(seoScore.score)}`} />
            <span className={`font-bold ${getScoreColor(seoScore.score)}`}>{seoScore.score}/100</span>
          </div>
        </div>
      </div>

      {/* SEO Checks */}
      <div className="p-4 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
        <div className="grid grid-cols-1 gap-2">
          {seoScore.checks.map((check, idx) => (
            <div key={idx} className="flex items-center gap-2 text-sm">
              {check.passed ? (
                <CheckCircle className="w-4 h-4 text-green-500" />
              ) : (
                <AlertCircle className="w-4 h-4 text-orange-500" />
              )}
              <span className={check.passed ? "text-slate-700 dark:text-slate-300" : "text-slate-500 dark:text-slate-400"}>
                {check.label}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* General SEO */}
      <div className="border-b border-slate-200 dark:border-slate-700">
        <button
          onClick={() => toggleSection("general")}
          className="w-full flex items-center justify-between p-4 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
        >
          <div className="flex items-center gap-2">
            <Search className="w-4 h-4 text-slate-500" />
            <span className="font-medium text-slate-700 dark:text-slate-200">SEO Geral</span>
          </div>
          {expanded.general ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
        
        {expanded.general && (
          <div className="p-4 pt-0 space-y-4">
            {/* Focus Keyword */}
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Palavra-chave Principal
              </label>
              <input
                type="text"
                value={data.focus_keyword || ""}
                onChange={(e) => handleChange("focus_keyword", e.target.value)}
                placeholder="Ex: apartamento em porto belo"
                className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
              />
            </div>

            {/* Meta Title */}
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Meta Título
                <span className={`ml-2 text-xs ${
                  (data.meta_title?.length || 0) > 60 ? "text-red-500" : "text-slate-400"
                }`}>
                  {data.meta_title?.length || 0}/60
                </span>
              </label>
              <input
                type="text"
                value={data.meta_title || ""}
                onChange={(e) => handleChange("meta_title", e.target.value)}
                placeholder="Título que aparece nos resultados de busca"
                className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
              />
            </div>

            {/* Meta Description */}
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Meta Descrição
                <span className={`ml-2 text-xs ${
                  (data.meta_description?.length || 0) > 160 ? "text-red-500" : "text-slate-400"
                }`}>
                  {data.meta_description?.length || 0}/160
                </span>
              </label>
              <textarea
                value={data.meta_description || ""}
                onChange={(e) => handleChange("meta_description", e.target.value)}
                placeholder="Descrição que aparece nos resultados de busca"
                rows={3}
                className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 resize-none"
              />
            </div>

            {/* Preview */}
            <div className="p-4 bg-slate-100 dark:bg-slate-800 rounded-lg">
              <p className="text-xs text-slate-500 mb-2">Pré-visualização no Google:</p>
              <div className="space-y-1">
                <p className="text-blue-600 dark:text-blue-400 text-lg font-medium truncate">
                  {data.meta_title || title || "Título do Post"}
                </p>
                <p className="text-green-700 dark:text-green-500 text-sm">
                  seusite.com.br › blog › {data.canonical_url || "slug-do-post"}
                </p>
                <p className="text-sm text-slate-600 dark:text-slate-400 line-clamp-2">
                  {data.meta_description || "Adicione uma meta descrição para ver a pré-visualização..."}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Social Media */}
      <div className="border-b border-slate-200 dark:border-slate-700">
        <button
          onClick={() => toggleSection("social")}
          className="w-full flex items-center justify-between p-4 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
        >
          <div className="flex items-center gap-2">
            <Globe className="w-4 h-4 text-slate-500" />
            <span className="font-medium text-slate-700 dark:text-slate-200">Redes Sociais</span>
          </div>
          {expanded.social ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
        
        {expanded.social && (
          <div className="p-4 pt-0 space-y-4">
            {/* OG Title */}
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Título para Facebook/LinkedIn
              </label>
              <input
                type="text"
                value={data.og_title || ""}
                onChange={(e) => handleChange("og_title", e.target.value)}
                placeholder="Deixe vazio para usar o meta título"
                className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
              />
            </div>

            {/* OG Description */}
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Descrição para Facebook/LinkedIn
              </label>
              <textarea
                value={data.og_description || ""}
                onChange={(e) => handleChange("og_description", e.target.value)}
                placeholder="Deixe vazio para usar a meta descrição"
                rows={2}
                className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 resize-none"
              />
            </div>

            {/* OG Image */}
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                Imagem de Compartilhamento
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={data.og_image || ""}
                  onChange={(e) => handleChange("og_image", e.target.value)}
                  placeholder="URL da imagem (1200x630px recomendado)"
                  className="flex-1 px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
                />
                {data.og_image && (
                  <div className="w-16 h-10 rounded overflow-hidden">
                    <img src={data.og_image} alt="OG" className="w-full h-full object-cover" />
                  </div>
                )}
              </div>
            </div>

            {/* Twitter */}
            <div className="pt-2 border-t border-slate-200 dark:border-slate-700">
              <p className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3 flex items-center gap-2">
                <Twitter className="w-4 h-4" /> Twitter/X
              </p>
              <div className="space-y-3">
                <input
                  type="text"
                  value={data.twitter_title || ""}
                  onChange={(e) => handleChange("twitter_title", e.target.value)}
                  placeholder="Título para Twitter"
                  className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
                />
                <textarea
                  value={data.twitter_description || ""}
                  onChange={(e) => handleChange("twitter_description", e.target.value)}
                  placeholder="Descrição para Twitter"
                  rows={2}
                  className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 resize-none"
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Advanced */}
      <div>
        <button
          onClick={() => toggleSection("advanced")}
          className="w-full flex items-center justify-between p-4 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
        >
          <div className="flex items-center gap-2">
            <Link2 className="w-4 h-4 text-slate-500" />
            <span className="font-medium text-slate-700 dark:text-slate-200">Avançado</span>
          </div>
          {expanded.advanced ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
        
        {expanded.advanced && (
          <div className="p-4 pt-0 space-y-4">
            {/* Canonical URL */}
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                URL Canônica
              </label>
              <input
                type="text"
                value={data.canonical_url || ""}
                onChange={(e) => handleChange("canonical_url", e.target.value)}
                placeholder="https://seusite.com.br/blog/slug"
                className="w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
              />
            </div>

            {/* Robots */}
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={data.robots_index !== false}
                  onChange={(e) => handleChange("robots_index", e.target.checked)}
                  className="w-4 h-4 rounded border-slate-300 text-primary focus:ring-primary"
                />
                <span className="text-sm text-slate-700 dark:text-slate-300">Indexar página</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={data.robots_follow !== false}
                  onChange={(e) => handleChange("robots_follow", e.target.checked)}
                  className="w-4 h-4 rounded border-slate-300 text-primary focus:ring-primary"
                />
                <span className="text-sm text-slate-700 dark:text-slate-300">Seguir links</span>
              </label>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SEOPanel;
